import {
  type FC, useCallback, useMemo,
} from 'react';
import { uniqueBy } from '~/_shared/utils/array/array.helpers';
import { BoundaryFill } from '~/boundary/settings/boundaryFill.type';
import { useFilteredLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { type ModalProps } from '~/modal/modalType.enum';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { useActiveBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '~/store/mapSettings/grouping/mapSettingsGrouping.selectors';
import {
  ExportImageLegendMode, type ExportImageParams,
} from '../../types/exportImage/exportImage';
import { ExportImageModalComponent } from './exportImageModal.component';
import { useExportImage } from './useExportImage';

export const ExportImageModalContainer: FC<ModalProps> = (props) => {
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const activeColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const filteredMarkersData = useFilteredLatLngSpreadsheetData();
  const lastBounds = useMapComponentLastBoundsSelector()?.bounds;
  const { isLoading, exportImage } = useExportImage();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const primaryBoundaryGroupId = useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector();

  const totalFilteredLocations = filteredMarkersData.visible.data.length;

  const filteredMarkersInBounds = useMemo(() => (
    filteredMarkersData.visible.data.filter(item => lastBounds?.contains(item))
  ), [filteredMarkersData.visible.data, lastBounds]);

  const onContinue = useCallback((exportProps: ExportImageParams) => {
    exportImage(exportProps);
  }, [exportImage]);

  const activeBGsExcludingNoFill = useMemo(() => (
    uniqueBy(
      activeBoundaryTerritoryGroups.filter(
        btg => btg.settings.boundaryTerritoryType !== BoundaryTerritoryType.Manual
          || btg.settings.boundaryFillType !== BoundaryFill.NoTerritoryFill,
      ).map(btg => ({
        id: btg.boundaryGroupId,
        displayName: getBoundaryTerritoryGroupName(btg),
      })),
      bg => bg.id,
    )
  ), [activeBoundaryTerritoryGroups, getBoundaryTerritoryGroupName]);

  const availableLegendOptions = useMemo(() => ([
    ...((activeColumns.length || activeBoundaryTerritoryGroups.length) ? [ExportImageLegendMode.MasterKey] : []),
    ...((activeColumns.length) ? [ExportImageLegendMode.Grouping] : []),
    ...((activeBGsExcludingNoFill.length) ? [ExportImageLegendMode.Boundary] : []),
  ]), [activeBGsExcludingNoFill.length, activeColumns.length, activeBoundaryTerritoryGroups]);

  const secondaryLegendChoicesConfig = useMemo(() => ({
    [ExportImageLegendMode.Boundary]: {
      minimumSelected: 1,
      defaultOption: activeBGsExcludingNoFill.find(bg => bg.id === primaryBoundaryGroupId)?.id,
      options: activeBGsExcludingNoFill.map(bg => ({
        name: bg.displayName,
        value: bg.id,
      })),
    },
  }), [activeBGsExcludingNoFill, primaryBoundaryGroupId]);

  return (
    <ExportImageModalComponent
      {...props}
      availableLegendOptions={availableLegendOptions}
      totalFilteredLocations={totalFilteredLocations}
      totalInBoundsLocations={filteredMarkersInBounds.length}
      onContinue={onContinue}
      isLoading={isLoading}
      secondaryLegendChoicesConfig={secondaryLegendChoicesConfig}
    />
  );
};
